/**
 * pages/IntroducerClientsPage.jsx
 * Route: /introducer/clients
 *
 * CRM-style view of all referrals the introducer can see.
 * Hierarchy-scoped via the same /api/introducer/referrals endpoint.
 * Notes are append-only (auditable): each note is a timestamped immutable entry.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import { useMobile } from '../hooks/useMobile';

const API = process.env.REACT_APP_API_URL || 'https://brokerconnect.co.uk/api';
const tok = () => localStorage.getItem('token');

/* ── helpers ──────────────────────────────────────────────── */
const STATUS_META = {
  submitted:  { label: 'Pending',   color: '#d97706', bg: '#fef3c7' },
  approved:   { label: 'Approved',  color: '#1d4ed8', bg: '#dbeafe' },
  rejected:   { label: 'Rejected',  color: '#dc2626', bg: '#fee2e2' },
  converted:  { label: 'Converted', color: '#065f46', bg: '#d1fae5' },
};
const sm = s => STATUS_META[s] || { label: s || 'Unknown', color: '#64748b', bg: '#f1f5f9' };

const CONTACT_META = {
  call_attempted: { label: 'Call Attempted', color: '#d97706' },
  call_made:      { label: 'Call Made',       color: '#1d4ed8' },
  no_reply:       { label: 'No Reply',        color: '#64748b' },
  meeting_booked: { label: 'Meeting Booked',  color: '#065f46' },
  no_discussion:  { label: 'No Discussion',   color: '#dc2626' },
};

const fmt = d => d ? new Date(d).toLocaleDateString('en-GB', { day:'numeric', month:'short', year:'numeric' }) : '—';
const fmtGBP = n => n != null ? `£${Number(n).toLocaleString('en-GB', { minimumFractionDigits:2, maximumFractionDigits:2 })}` : null;

/* ── StatusBadge ──────────────────────────────────────────── */
function StatusBadge({ status }) {
  const { label, color, bg } = sm(status);
  return (
    <span style={{
      fontSize: 11, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase',
      color, background: bg, borderRadius: 99, padding: '3px 10px',
      whiteSpace: 'nowrap',
    }}>{label}</span>
  );
}

/* ── Main page ────────────────────────────────────────────── */
export default function IntroducerClientsPage() {
  const navigate = useNavigate();
  const mobile   = useMobile();
  const ml       = mobile ? 0 : 70;

  const [clients,  setClients]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState('');
  const [query,    setQuery]    = useState('');
  const [filter,   setFilter]   = useState('all');

  const fetchClients = useCallback(async () => {
    const t = tok();
    if (!t) { navigate('/login'); return; }
    try {
      const r = await fetch(`${API}/introducer/referrals`, { headers: { Authorization: `Bearer ${t}` } });
      if (!r.ok) throw new Error('Failed to load');
      const data = await r.json();
      setClients(data.referrals || []);
    } catch {
      setError('Failed to load client records.');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => { fetchClients(); }, [fetchClients]);


  /* filter & search */
  const visible = clients.filter(c => {
    if (filter !== 'all' && c.lead_status !== filter) return false;
    if (!query.trim()) return true;
    const q = query.toLowerCase();
    return (c.client_name || '').toLowerCase().includes(q)
      || (c.client_email || '').toLowerCase().includes(q)
      || (c.client_phone || '').includes(q);
  });

  /* status counts */
  const counts = clients.reduce((acc, c) => {
    acc[c.lead_status] = (acc[c.lead_status] || 0) + 1;
    return acc;
  }, {});

  const FILTER_TABS = [
    { key: 'all',       label: 'All',       count: clients.length },
    { key: 'submitted', label: 'Pending',   count: counts.submitted || 0 },
    { key: 'approved',  label: 'Approved',  count: counts.approved  || 0 },
    { key: 'converted', label: 'Converted', count: counts.converted || 0 },
    { key: 'rejected',  label: 'Rejected',  count: counts.rejected  || 0 },
  ];

  /* ── render ── */
  if (loading) return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#f1f5f9' }}>
      <Sidebar />
      <div style={{ flex: 1, marginLeft: ml, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center', color: '#64748b' }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>👤</div>
          <div style={{ fontSize: 15, fontWeight: 600 }}>Loading clients…</div>
        </div>
      </div>
    </div>
  );

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#f1f5f9' }}>
      <Sidebar />
      <div style={{ flex: 1, marginLeft: ml, minWidth: 0 }}>

        {/* ─── HERO ─── */}
        <div style={{
          background: 'linear-gradient(135deg, #0e1b2c 0%, #1a3a5c 55%, #10b981 100%)',
          padding: mobile ? '28px 16px 36px' : '36px 40px 48px',
          position: 'relative', overflow: 'hidden',
        }}>
          <div style={{ position: 'absolute', top: -60, right: -60, width: 220, height: 220, borderRadius: '50%', background: 'rgba(255,255,255,0.03)', pointerEvents: 'none' }} />
          <div style={{ position: 'absolute', bottom: -30, right: '20%', width: 130, height: 130, borderRadius: '50%', background: 'rgba(255,255,255,0.04)', pointerEvents: 'none' }} />

          <div style={{ position: 'relative', zIndex: 1 }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'rgba(255,255,255,0.1)', borderRadius: 99, padding: '4px 12px', marginBottom: 12 }}>
              <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', color: 'rgba(255,255,255,0.65)', textTransform: 'uppercase' }}>Introducer Portal</span>
              <span style={{ width: 4, height: 4, borderRadius: '50%', background: '#34d399', display: 'inline-block' }} />
              <span style={{ fontSize: 10, fontWeight: 600, color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Clients</span>
            </div>
            <h1 style={{ margin: 0, fontSize: mobile ? 24 : 30, fontWeight: 800, color: '#fff', letterSpacing: '-0.02em', lineHeight: 1.15 }}>Your Clients</h1>
            <p style={{ margin: '8px 0 20px', fontSize: 13, color: 'rgba(255,255,255,0.55)' }}>View and update every client record you've referred</p>

            {/* KPI strip */}
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              {[
                { label: 'Total Referred', value: clients.length, icon: '👤' },
                { label: 'In Pipeline',    value: (counts.approved || 0) + (counts.converted || 0), icon: '🔄' },
                { label: 'Converted',      value: counts.converted || 0, icon: '✅' },
              ].map(k => (
                <div key={k.label} style={{
                  background: 'rgba(255,255,255,0.07)', backdropFilter: 'blur(12px)',
                  border: '1px solid rgba(255,255,255,0.12)', borderRadius: 12,
                  padding: '12px 20px', minWidth: 110,
                }}>
                  <div style={{ fontSize: 18, marginBottom: 4 }}>{k.icon}</div>
                  <div style={{ fontSize: 22, fontWeight: 800, color: '#fff', letterSpacing: '-0.02em' }}>{k.value}</div>
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', fontWeight: 500, marginTop: 2 }}>{k.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ─── TOOLBAR ─── */}
        <div style={{ padding: mobile ? '16px 16px 0' : '24px 40px 0' }}>
          {error && (
            <div style={{ background: '#fee2e2', color: '#991b1b', padding: '12px 16px', borderRadius: 10, marginBottom: 16, fontSize: 14 }}>{error}</div>
          )}

          {/* Search */}
          <div style={{ position: 'relative', marginBottom: 16, maxWidth: 400 }}>
            <span style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', fontSize: 14, color: '#94a3b8' }}>🔍</span>
            <input
              type="text"
              placeholder="Search by name, email or phone…"
              value={query}
              onChange={e => setQuery(e.target.value)}
              style={{
                width: '100%', boxSizing: 'border-box',
                padding: '10px 14px 10px 38px',
                border: '1px solid #e2e8f0', borderRadius: 10,
                fontSize: 14, color: '#0e1b2c', background: '#fff',
                outline: 'none',
              }}
            />
          </div>

          {/* Filter tabs */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {FILTER_TABS.map(tab => {
              const active = filter === tab.key;
              const meta = tab.key !== 'all' ? sm(tab.key) : { color: '#0e1b2c', bg: '#e2e8f0' };
              return (
                <button
                  key={tab.key}
                  onClick={() => setFilter(tab.key)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    padding: '7px 14px', borderRadius: 99, fontSize: 13, fontWeight: 600,
                    border: 'none', cursor: 'pointer', transition: 'all 0.15s',
                    background: active ? (tab.key === 'all' ? '#0e1b2c' : meta.bg) : '#fff',
                    color: active ? (tab.key === 'all' ? '#fff' : meta.color) : '#64748b',
                    boxShadow: active ? '0 2px 8px rgba(0,0,0,0.1)' : '0 1px 3px rgba(0,0,0,0.06)',
                  }}
                >
                  {tab.label}
                  <span style={{
                    fontSize: 11, fontWeight: 700,
                    background: active ? 'rgba(0,0,0,0.12)' : '#f1f5f9',
                    color: active ? 'inherit' : '#94a3b8',
                    borderRadius: 99, padding: '1px 7px',
                  }}>{tab.count}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* ─── CLIENT LIST ─── */}
        <div style={{ padding: mobile ? '16px' : '20px 40px 40px' }}>
          {visible.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '56px 24px', background: '#fff', borderRadius: 16, border: '1px dashed #e2e8f0', color: '#94a3b8', marginTop: 8 }}>
              <div style={{ fontSize: 36, marginBottom: 12 }}>🔍</div>
              <div style={{ fontSize: 15, fontWeight: 600, color: '#64748b', marginBottom: 6 }}>
                {clients.length === 0 ? 'No referrals yet' : 'No matching clients'}
              </div>
              <div style={{ fontSize: 13 }}>
                {clients.length === 0 ? 'Clients will appear here once you submit referrals.' : 'Try adjusting your search or filter.'}
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 16 }}>
              {visible.map(client => {
                const meta   = sm(client.lead_status);
                return (
                  <div
                    key={client.id}
                    onClick={() => navigate('/introducer/referrals?open=' + client.id)}
                    style={{
                      background: '#fff',
                      border: '1px solid #e2e8f0',
                      borderRadius: 14,
                      padding: mobile ? '14px 16px' : '16px 24px',
                      cursor: 'pointer',
                      display: 'flex', alignItems: 'center', gap: 16,
                      boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                      transition: 'all 0.15s',
                    }}
                  >
                    {/* Avatar */}
                    <div style={{
                      width: 44, height: 44, borderRadius: '50%', flexShrink: 0,
                      background: `linear-gradient(135deg, ${meta.bg}, ${meta.color}22)`,
                      border: `2px solid ${meta.bg}`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 16, fontWeight: 800, color: meta.color,
                    }}>
                      {(client.client_name || '?').charAt(0).toUpperCase()}
                    </div>

                    {/* Info */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 15, fontWeight: 700, color: '#0e1b2c', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {client.client_name || 'Unnamed'}
                        </span>
                        <StatusBadge status={client.lead_status} />
                        {client.deal_stage_name && (
                          <span style={{
                            fontSize: 11, fontWeight: 600, color: '#fff',
                            background: client.deal_stage_colour || '#64748b',
                            borderRadius: 99, padding: '2px 8px', whiteSpace: 'nowrap',
                          }}>{client.deal_stage_name}</span>
                        )}
                      </div>
                      <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {[client.client_email, client.client_phone].filter(Boolean).join(' · ')}
                        {client.submitted_by_name && ` · via ${client.submitted_by_name}`}
                      </div>
                      {client.notes && (
                        <div style={{ fontSize: 12, color: '#64748b', marginTop: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontStyle: 'italic' }}>
                          📝 {client.notes}
                        </div>
                      )}
                    </div>

                    {/* Right side */}
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      {client.my_commission != null && (
                        <div style={{ fontSize: 14, fontWeight: 700, color: '#10b981' }}>{fmtGBP(client.my_commission)}</div>
                      )}
                      <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{fmt(client.created_at)}</div>
                      <div style={{ fontSize: 12, color: '#10b981', marginTop: 4, fontWeight: 600 }}>
                        {'View referral →'}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

      </div>

    </div>
  );
}
